import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Avatar,
  Chip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useAuth } from "../../contexts/AuthContext";
import { useAPI } from "../../hooks/useAPI";
import { useUI } from "../../contexts/UIContext";
import CallToolbar from "../../components/simple/CallToolbar";
import CallScreenSidePanel from "../../components/complex/CallScreenSidePanel";
import AudioOnlyAvatar from "../../components/CallScreen/AudioOnlyAvatar";
import ConnectionBanner from "../../components/CallScreen/ConnectionBanner";
import coachImage from "../../assets/coach-test-img.jpg";
import theme from "../../Theme";

// ── TEMP DEBUG helpers — read ?audioOnly and ?banner URL params ──────────────
function getDebugAudioOnlyState() {
  const params = new URLSearchParams(window.location.search);
  const audioOnly = params.get("audioOnly");
  const banner = params.get("banner");
  if (audioOnly === "true")   return { isAudioOnly: true,  bannerState: "pre_call_degraded" };
  if (audioOnly === "avatar") return { isAudioOnly: true,  bannerState: null }; // silent fallback
  if (banner === "pre_call")  return { isAudioOnly: false, bannerState: "pre_call_degraded" };
  if (banner === "mid_call")  return { isAudioOnly: false, bannerState: "mid_call_instability" };
  return { isAudioOnly: false, bannerState: null };
}

// ── State cycles demo (?demo=true) ────────────────────────────────────────────
const DEMO_SEQUENCE = [
  { label: "Audio-only · Idle",            isAudioOnly: true,  bannerState: "pre_call_degraded", userSpeaking: false, avatarSpeaking: false },
  { label: "Audio-only · User speaking",   isAudioOnly: true,  bannerState: "pre_call_degraded", userSpeaking: true,  avatarSpeaking: false },
  { label: "Audio-only · Avatar speaking", isAudioOnly: true,  bannerState: "pre_call_degraded", userSpeaking: false, avatarSpeaking: true  },
  { label: "Banner · Pre-call degraded",   isAudioOnly: true,  bannerState: "pre_call_degraded",       userSpeaking: false, avatarSpeaking: false },
  { label: "Banner · Mid-call instability",isAudioOnly: true,  bannerState: "mid_call_instability",    userSpeaking: false, avatarSpeaking: false },
  { label: "Video mode · No banner",       isAudioOnly: false, bannerState: null,                      userSpeaking: false, avatarSpeaking: false },
];
const DEMO_INTERVAL_MS = 5000;

// ── Flows demo (?demo=flows) — two 15-second user journey walkthroughs ────────
//
// Flow 1 — Pre-Call Unstable:
//   Connecting → connection detected as unstable → audio fallback + orange toast
//
// Flow 2 — Mid-Call Unstable:
//   Video call in progress → connection drops → red toast → smooth switch to audio fallback
//
const FLOWS_TOTAL_MS = 45000; // 3 × 15 s

const FLOW_STEPS = [
  // ── Flow 1: Pre-Call Unstable (0 – 15 s) ─────────────────────────────────
  {
    at: 0,
    flow: 1, flowLabel: "Flow 1 · Pre-Call Unstable", stepLabel: "Connecting to session…",
    isConnecting: true,  isConnected: false,
    isAudioOnly: false, bannerState: null,
    userSpeaking: false, avatarSpeaking: false,
  },
  {
    // Connection detected as unstable before session starts → go straight to audio fallback
    at: 3500,
    flow: 1, flowLabel: "Flow 1 · Pre-Call Unstable", stepLabel: "Unstable connection — switching to audio fallback",
    isConnecting: false, isConnected: true,
    isAudioOnly: true, bannerState: "pre_call_degraded", // orange toast appears
    userSpeaking: false, avatarSpeaking: false,
  },
  // Orange toast auto-dismisses ~8.5 s (5 s after it appears at 3.5 s)
  // Audio fallback stays for rest of Flow 1

  // ── Flow 2: Mid-Call Unstable (15 – 30 s) ────────────────────────────────
  {
    at: 15000,
    flow: 2, flowLabel: "Flow 2 · Mid-Call Unstable", stepLabel: "Video call in progress",
    isConnecting: false, isConnected: true,
    isAudioOnly: false, bannerState: null,
    userSpeaking: false, avatarSpeaking: false,
  },
  {
    // Connection drops — red toast appears while still in video mode
    at: 20000,
    flow: 2, flowLabel: "Flow 2 · Mid-Call Unstable", stepLabel: "Connection dropped — notifying user",
    isConnecting: false, isConnected: true,
    isAudioOnly: false, bannerState: "mid_call_instability", // red toast appears
    userSpeaking: false, avatarSpeaking: false,
  },
  {
    // 1.5 s later: smooth transition to audio fallback (toast timer keeps running)
    at: 21500,
    flow: 2, flowLabel: "Flow 2 · Mid-Call Unstable", stepLabel: "Switching to audio fallback",
    isConnecting: false, isConnected: true,
    isAudioOnly: true, bannerState: "mid_call_instability", // same state → toast continues
    userSpeaking: false, avatarSpeaking: false,
  },
  // Red toast auto-dismisses ~25 s (5 s after it appears at 20 s)
  // Audio fallback stays until 30 s

  // ── Flow 3: Aura Ring States (30 – 45 s) ─────────────────────────────────
  {
    at: 30000,
    flow: 3, flowLabel: "Flow 3 · Aura States", stepLabel: "Idle — no one speaking",
    isConnecting: false, isConnected: true,
    isAudioOnly: true, bannerState: null,
    userSpeaking: false, avatarSpeaking: false,
  },
  {
    at: 35000,
    flow: 3, flowLabel: "Flow 3 · Aura States", stepLabel: "User speaking — purple aura",
    isConnecting: false, isConnected: true,
    isAudioOnly: true, bannerState: null,
    userSpeaking: true, avatarSpeaking: false,
  },
  {
    at: 40000,
    flow: 3, flowLabel: "Flow 3 · Aura States", stepLabel: "Avatar speaking — cyan aura",
    isConnecting: false, isConnected: true,
    isAudioOnly: true, bannerState: null,
    userSpeaking: false, avatarSpeaking: true,
  },
  // Loops back to Flow 1 at 45 s
];
// ─────────────────────────────────────────────────────────────────────────────

// Mock transcript lines that populate progressively during the session
const MOCK_TRANSCRIPT = [
  { role: "avatar", text: "Hi! I'm Alex, your AI coaching partner. Welcome to this session on giving feedback. How are you feeling today?" },
  { role: "user",   text: "I'm good, thanks. A bit nervous about the conversation we're going to practice." },
  { role: "avatar", text: "That's completely understandable. Feedback conversations can feel uncomfortable. What specifically are you finding challenging?" },
  { role: "user",   text: "I think it's the part where I have to tell someone their performance isn't meeting expectations." },
  { role: "avatar", text: "That's one of the hardest parts of leadership. Let's work through it together. I'll play the role of a team member. Ready to begin?" },
  { role: "user",   text: "Yes, let's do it." },
  { role: "avatar", text: "Great. Remember to use the SBI model — Situation, Behaviour, Impact. Whenever you're ready." },
];

// Pulsing ring animation for the avatar when "speaking"
const pulseKeyframes = `
  @keyframes gnPulse {
    0%   { transform: scale(1);    opacity: 0.6; }
    50%  { transform: scale(1.12); opacity: 0.2; }
    100% { transform: scale(1);    opacity: 0.6; }
  }
  @keyframes gnPulse2 {
    0%   { transform: scale(1);    opacity: 0.4; }
    50%  { transform: scale(1.22); opacity: 0.1; }
    100% { transform: scale(1);    opacity: 0.4; }
  }
`;

export default function MockCallScreen() {
  const { scenario_id } = useParams();
  const navigate = useNavigate();
  const { authToken, loggedInUser } = useAuth();
  const { callAPI } = useAPI();
  const { isMobile, setShowFooter, setSideMenuOpen, setShowCrumbs } = useUI();

  const [scenarioData, setScenarioData] = useState(null);
  const [isConnecting, setIsConnecting] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [avatarSpeaking, setAvatarSpeaking] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [videoEnabled, setVideoEnabled] = useState(false);
  const [showSidePanel, setShowSidePanel] = useState(false);
  const [panelValue, setPanelValue] = useState(0);
  const [transcript, setTranscript] = useState([]);
  const [videoPosition, setVideoPosition] = useState({ x: 20, y: 20 });
  const [isDragging, setIsDragging] = useState(false);
  const [subTitlesEnabled, setSubTitlesEnabled] = useState(false);

  const videoRef = useRef(null);
  const startXRef = useRef(0);
  const startYRef = useRef(0);
  const transcriptIndexRef = useRef(0);
  const speakingTimerRef = useRef(null);
  const transcriptTimerRef = useRef(null);

  // Hide nav chrome like the real call screen
  useEffect(() => {
    setShowFooter(false);
    setShowCrumbs(false);
    if (isMobile) setSideMenuOpen(false);
    return () => {
      setShowFooter(true);
      setShowCrumbs(true);
    };
  }, []);

  // Fetch scenario data
  useEffect(() => {
    if (!scenario_id || !authToken) return;
    callAPI(`scenario?scenario_id=${scenario_id}`, { token: authToken })
      .then(setScenarioData)
      .catch(console.error);
  }, [scenario_id, authToken]);

  // Simulate connection sequence (skipped in flows mode — it controls its own timing)
  useEffect(() => {
    if (isFlowsMode) return;
    const t = setTimeout(() => {
      setIsConnecting(false);
      setIsConnected(true);
      startMockSession();
    }, 2200);
    return () => clearTimeout(t);
  }, []);

  // Simulate the avatar speaking and transcript populating
  const startMockSession = () => {
    const addNextLine = () => {
      const idx = transcriptIndexRef.current;
      if (idx >= MOCK_TRANSCRIPT.length) return;

      const line = MOCK_TRANSCRIPT[idx];
      const isAvatar = line.role === "avatar";

      if (isAvatar) setAvatarSpeaking(true);

      setTranscript((prev) => [
        ...prev,
        {
          author_name: isAvatar ? "Alex" : "You",
          message: line.text,
          ai_generated: isAvatar,
          is_user: !isAvatar,
        },
      ]);

      transcriptIndexRef.current = idx + 1;

      const speakDuration = line.text.length * 45; // rough ms per char
      const pauseBetween = 2500;

      if (isAvatar) {
        speakingTimerRef.current = setTimeout(() => {
          setAvatarSpeaking(false);
        }, speakDuration);
      }

      transcriptTimerRef.current = setTimeout(
        addNextLine,
        speakDuration + pauseBetween,
      );
    };

    // Short delay before first line
    setTimeout(addNextLine, 800);
  };

  // Clean up timers on unmount
  useEffect(() => {
    return () => {
      clearTimeout(speakingTimerRef.current);
      clearTimeout(transcriptTimerRef.current);
    };
  }, []);

  // End simulation → go back to training program
  const handleEndSimulation = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  // Webcam toggle
  const handleToggleVideo = async () => {
    const next = !videoEnabled;
    setVideoEnabled(next);
    if (next) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        if (videoRef.current) videoRef.current.srcObject = stream;
      } catch {
        setVideoEnabled(false);
      }
    } else {
      const stream = videoRef.current?.srcObject;
      if (stream) stream.getTracks().forEach((t) => t.stop());
      if (videoRef.current) videoRef.current.srcObject = null;
    }
  };

  // Dragging logic for the user video pip
  const getClient = (e) =>
    e.touches?.length
      ? { x: e.touches[0].clientX, y: e.touches[0].clientY }
      : { x: e.clientX, y: e.clientY };

  const handleDown = (e) => {
    const { x, y } = getClient(e);
    setIsDragging(true);
    startXRef.current = x - videoPosition.x;
    startYRef.current = y - videoPosition.y;
  };

  useEffect(() => {
    const handleMove = (e) => {
      if (!isDragging) return;
      const { x, y } = getClient(e);
      setVideoPosition({ x: x - startXRef.current, y: y - startYRef.current });
    };
    const handleUp = () => setIsDragging(false);

    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    window.addEventListener("touchmove", handleMove);
    window.addEventListener("touchend", handleUp);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
      window.removeEventListener("touchmove", handleMove);
      window.removeEventListener("touchend", handleUp);
    };
  }, [isDragging]);

  function getInitials(user) {
    if (!user) return "?";
    return `${user.first_name?.[0] ?? ""}${user.last_name?.[0] ?? ""}`.toUpperCase();
  }

  const avatarName = scenarioData?.avatar_persona?.name || "Alex";
  const avatarInitial = avatarName[0]?.toUpperCase() || "A";

  // TEMP DEBUG: resolve audio-only state from URL params
  const { isAudioOnly, bannerState } = getDebugAudioOnlyState();
  const [audioOnlyActive, setAudioOnlyActive] = useState(isAudioOnly);
  const [activeBanner, setActiveBanner] = useState(bannerState);

  // ?speaking=idle | user | avatar  — pins the aura ring state for testing.
  // If omitted, alternates automatically every 3s.
  const speakingParam = new URLSearchParams(window.location.search).get("speaking");
  const [mockUserSpeaking, setMockUserSpeaking] = useState(speakingParam === "user");
  const [mockAvatarSpeaking, setMockAvatarSpeaking] = useState(speakingParam === "avatar");

  // ── Demo modes ────────────────────────────────────────────────────────────────
  const demoParam    = new URLSearchParams(window.location.search).get("demo");
  const isDemoMode   = demoParam === "true";
  const isFlowsMode  = demoParam === "flows";
  const isAnyDemo    = isDemoMode || isFlowsMode;

  // ?demo=true — state cycle
  const [demoStep, setDemoStep] = useState(0);
  const [demoProgress, setDemoProgress] = useState(0);
  const progressIntervalRef = useRef(null);

  // ?demo=flows — user journey flows
  const [flowsLabel,     setFlowsLabel]     = useState("");
  const [flowsStepLabel, setFlowsStepLabel] = useState("");
  const [flowsProgress,  setFlowsProgress]  = useState(0);
  const flowsStartTime   = useRef(null);
  const flowsStepTimers  = useRef([]);
  const flowsProgRef     = useRef(null);
  // ─────────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!audioOnlyActive || speakingParam || isAnyDemo) return; // demo controls speaking — don't auto-cycle
    // Auto-cycle: idle → avatar → user → repeat
    const states = [
      { user: false, avatar: false }, // idle
      { user: false, avatar: true  }, // avatar speaking
      { user: true,  avatar: false }, // user speaking
    ];
    let i = 0;
    const interval = setInterval(() => {
      i = (i + 1) % states.length;
      setMockUserSpeaking(states[i].user);
      setMockAvatarSpeaking(states[i].avatar);
    }, 3000);
    return () => clearInterval(interval);
  }, [audioOnlyActive, speakingParam, isDemoMode]);

  const startProgress = () => {
    if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    setDemoProgress(0);
    progressIntervalRef.current = setInterval(() => {
      setDemoProgress((p) => Math.min(p + (100 / (DEMO_INTERVAL_MS / 100)), 100));
    }, 100);
  };

  useEffect(() => {
    if (!isDemoMode) return;

    const applyStep = (step) => {
      const s = DEMO_SEQUENCE[step];
      setAudioOnlyActive(s.isAudioOnly);
      setActiveBanner(s.bannerState);
      setMockUserSpeaking(s.userSpeaking);
      setMockAvatarSpeaking(s.avatarSpeaking);
      startProgress();
    };

    applyStep(0);

    const stepInterval = setInterval(() => {
      setDemoStep((prev) => {
        const next = (prev + 1) % DEMO_SEQUENCE.length;
        applyStep(next);
        return next;
      });
    }, DEMO_INTERVAL_MS);

    return () => {
      clearInterval(stepInterval);
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    };
  }, [isDemoMode]);

  // ── Flows demo effect ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isFlowsMode) return;

    const applyFlowStep = (step) => {
      setIsConnecting(step.isConnecting);
      setIsConnected(step.isConnected);
      setAudioOnlyActive(step.isAudioOnly);
      setActiveBanner(step.bannerState);
      setMockUserSpeaking(step.userSpeaking);
      setMockAvatarSpeaking(step.avatarSpeaking);
      setFlowsLabel(step.flowLabel);
      setFlowsStepLabel(step.stepLabel);
    };

    const scheduleSteps = () => {
      // Clear previous step timers
      flowsStepTimers.current.forEach(clearTimeout);
      flowsStepTimers.current = [];
      flowsStartTime.current = Date.now();

      FLOW_STEPS.forEach((step) => {
        const t = setTimeout(() => applyFlowStep(step), step.at);
        flowsStepTimers.current.push(t);
      });
    };

    // Progress bar — tracks 0–100% within the current 15 s window
    flowsProgRef.current = setInterval(() => {
      if (!flowsStartTime.current) return;
      const elapsed = (Date.now() - flowsStartTime.current) % FLOWS_TOTAL_MS;
      const withinFlow = elapsed % 15000;
      setFlowsProgress((withinFlow / 15000) * 100);
    }, 80);

    // Start immediately, then loop every 30 s
    scheduleSteps();
    const loopInterval = setInterval(scheduleSteps, FLOWS_TOTAL_MS);

    return () => {
      flowsStepTimers.current.forEach(clearTimeout);
      clearInterval(loopInterval);
      if (flowsProgRef.current) clearInterval(flowsProgRef.current);
    };
  }, [isFlowsMode]);
  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <Box
      id="callscreen-container"
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: "100%",
        height: "100%",
        backgroundColor: "transparent",
        "&:hover .sim-name-overlay": { opacity: 1 },
        "& .sim-name-overlay": {
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          padding: "15px",
          background: "linear-gradient(-180deg,rgba(0,0,0,1) 36%,rgba(255,255,255,0) 100%)",
          zIndex: 9999,
          opacity: 0,
          transition: "all 0.2s ease",
        },
        "& #video": {
          width: "182px",
          height: "182px",
          [theme.breakpoints.down("md")]: { width: "103px", height: "103px" },
        },
        "& #avatar-pic": {
          width: "182px",
          height: "165px",
          [theme.breakpoints.down("md")]: { width: "103px", height: "96px" },
        },
      }}
    >
      {/* Mock mode badge */}
      <Chip
        label="Preview Mode — No live connection"
        size="small"
        sx={{
          position: "absolute",
          top: 12,
          right: 12,
          zIndex: 10000,
          backgroundColor: "rgba(0,0,0,0.55)",
          color: "#fff",
          fontSize: "11px",
          backdropFilter: "blur(6px)",
          border: "1px solid rgba(255,255,255,0.2)",
        }}
      />

      {/* Main call area */}
      <Paper
        elevation={3}
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: "1350px",
          aspectRatio: "16 / 9",
          borderRadius: 2,
          backgroundColor: "#231F20",
          border: "1.5px solid #211F45",
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          overflow: "hidden",
          [theme.breakpoints.down("md")]: {
            aspectRatio: "9 / 16",
            height: "calc(100dvh - 70px)",
            borderRadius: 0,
            position: "absolute",
            bottom: 0,
          },
        }}
      >
        {/* Connection / avatar banner */}
        <ConnectionBanner state={activeBanner} />

        {/* ── Demo HUD — ?demo=true state cycle ── */}
        {isDemoMode && (
          <Box
            sx={{
              position: "absolute",
              bottom: 100,
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 9999,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "8px",
              pointerEvents: "none",
            }}
          >
            <Box
              sx={{
                backgroundColor: "rgba(0,0,0,0.65)",
                backdropFilter: "blur(8px)",
                border: "1px solid rgba(255,255,255,0.15)",
                borderRadius: "8px",
                px: "14px",
                py: "6px",
                display: "flex",
                alignItems: "center",
                gap: "10px",
              }}
            >
              <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: "11px", fontFamily: "monospace" }}>
                {demoStep + 1}/{DEMO_SEQUENCE.length}
              </Typography>
              <Typography sx={{ color: "#fff", fontSize: "13px", fontWeight: 600, letterSpacing: "0.02em" }}>
                {DEMO_SEQUENCE[demoStep].label}
              </Typography>
            </Box>
            <Box sx={{ width: "220px", height: "3px", borderRadius: "2px", backgroundColor: "rgba(255,255,255,0.15)", overflow: "hidden" }}>
              <Box sx={{ height: "100%", width: `${demoProgress}%`, backgroundColor: "#01ECFF", borderRadius: "2px", transition: "width 0.1s linear" }} />
            </Box>
          </Box>
        )}

        {/* ── Flows HUD — ?demo=flows user journey walkthrough ── */}
        {isFlowsMode && flowsLabel && (
          <Box
            sx={{
              position: "absolute",
              bottom: 100,
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 9999,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "8px",
              pointerEvents: "none",
              minWidth: "320px",
            }}
          >
            {/* Flow name + step description */}
            <Box
              sx={{
                backgroundColor: "rgba(0,0,0,0.70)",
                backdropFilter: "blur(10px)",
                border: "1px solid rgba(255,255,255,0.12)",
                borderRadius: "10px",
                px: "16px",
                py: "10px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "4px",
                width: "100%",
              }}
            >
              {/* Flow badge */}
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <Box sx={{
                  backgroundColor: flowsLabel.startsWith("Flow 1") ? "rgba(247,144,9,0.25)"
                    : flowsLabel.startsWith("Flow 2") ? "rgba(240,68,56,0.25)"
                    : "rgba(1,236,255,0.15)",
                  border: `1px solid ${flowsLabel.startsWith("Flow 1") ? "rgba(247,144,9,0.5)"
                    : flowsLabel.startsWith("Flow 2") ? "rgba(240,68,56,0.5)"
                    : "rgba(1,236,255,0.4)"}`,
                  borderRadius: "4px",
                  px: "6px",
                  py: "1px",
                }}>
                  <Typography sx={{
                    color: flowsLabel.startsWith("Flow 1") ? "#F79009"
                      : flowsLabel.startsWith("Flow 2") ? "#F04438"
                      : "#01ECFF",
                    fontSize: "10px", fontWeight: 700, fontFamily: "monospace", letterSpacing: "0.05em",
                  }}>
                    {flowsLabel.startsWith("Flow 1") ? "FLOW 1 · 15s"
                      : flowsLabel.startsWith("Flow 2") ? "FLOW 2 · 15s"
                      : "FLOW 3 · 15s"}
                  </Typography>
                </Box>
                <Typography sx={{ color: "rgba(255,255,255,0.6)", fontSize: "11px", fontFamily: "monospace" }}>
                  {flowsLabel.includes("Pre-Call") ? "Pre-Call Unstable"
                    : flowsLabel.includes("Mid-Call") ? "Mid-Call Unstable"
                    : "Aura Ring States"}
                </Typography>
              </Box>
              {/* Current step */}
              <Typography sx={{ color: "#fff", fontSize: "12px", fontWeight: 500, letterSpacing: "0.01em", textAlign: "center" }}>
                {flowsStepLabel}
              </Typography>
            </Box>

            {/* Progress bar — 0–100% within each 15 s window */}
            <Box sx={{ width: "100%", height: "3px", borderRadius: "2px", backgroundColor: "rgba(255,255,255,0.12)", overflow: "hidden" }}>
              <Box
                sx={{
                  height: "100%",
                  width: `${flowsProgress}%`,
                  backgroundColor: flowsLabel.startsWith("Flow 1") ? "#F79009"
                    : flowsLabel.startsWith("Flow 2") ? "#F04438"
                    : "#01ECFF",
                  borderRadius: "2px",
                  transition: "width 0.08s linear",
                }}
              />
            </Box>
          </Box>
        )}

        {!isMobile && (
          <div className="sim-name-overlay">{scenarioData?.name}</div>
        )}

        {/* ── BACKGROUND — brand gradient: 25% turquoise → navy → 25% purple ── */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(135deg, rgba(1, 236, 255, 0.20) 0%, #030039 48%, rgba(150, 46, 255, 0.22) 100%)",
          }}
        />

        {/* ── CONNECTING OVERLAY ── */}
        {(isConnecting || !isConnected) && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              backgroundColor: "rgba(35,31,32,0.9)",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 20,
            }}
          >
            <CircularProgress size={60} sx={{ color: "#fff" }} />
            <Typography variant="h5" mt={3} color="white">
              Connecting to simulation...
            </Typography>
          </Box>
        )}

        {/* ── AI AVATAR PLACEHOLDER (shown when connected, video mode) ── */}
        {isConnected && !audioOnlyActive && (
          <Box
            component="img"
            src={coachImage}
            alt="AI Coach video feed"
            sx={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center 15%",
              zIndex: 5,
              pointerEvents: "none",
            }}
          />
        )}

        {/* ── AUDIO-ONLY AVATAR (shown when ?audioOnly param is set) ── */}
        {isConnected && audioOnlyActive && (
          <AudioOnlyAvatar
            isUserSpeaking={mockUserSpeaking}
            isAvatarSpeaking={mockAvatarSpeaking}
            name={avatarName}
          />
        )}

        {/* ── USER VIDEO / INITIALS PiP — hidden in audio-only ── */}
        {isConnected && !audioOnlyActive && (
          videoEnabled ? (
            <video
              ref={videoRef}
              id="video"
              autoPlay
              playsInline
              muted
              onMouseDown={handleDown}
              onTouchStart={handleDown}
              style={{
                position: "absolute",
                top: videoPosition.y,
                left: videoPosition.x,
                width: 182,
                height: 182,
                objectFit: "cover",
                borderRadius: 8,
                cursor: isDragging ? "grabbing" : "grab",
                backgroundColor: "black",
                zIndex: 500,
                boxShadow: "0 2px 10px rgba(0,0,0,0.4)",
                transform: "rotateY(180deg)",
                transition: isDragging ? "none" : "top 0.2s, left 0.2s",
              }}
            />
          ) : (
            <Avatar
              id="avatar-pic"
              onMouseDown={handleDown}
              onTouchStart={handleDown}
              sx={{
                position: "absolute",
                top: videoPosition.y,
                left: videoPosition.x,
                width: 182,
                height: 182,
                fontSize: 48,
                fontWeight: 600,
                background: "linear-gradient(135deg, #1a3a5c 0%, #0d1f3c 100%)",
                border: "2px solid rgba(255,255,255,0.15)",
                borderRadius: "8px",
                cursor: isDragging ? "grabbing" : "grab",
                zIndex: 500,
                boxShadow: "0 2px 10px rgba(0,0,0,0.4)",
              }}
            >
              {getInitials(loggedInUser)}
            </Avatar>
          )
        )}

        {/* ── TOOLBAR + SIDE PANEL ── */}
        {isConnected && (
          <Box
            sx={{
              height: "100%",
              width: "100%",
              display: "flex",
              gap: 15,
              justifyContent: showSidePanel ? "space-between" : "center",
              alignItems: "flex-end",
              zIndex: 30,
              boxSizing: "border-box",
              padding: theme.customSpacing["2xl"],
            }}
          >
            <CallToolbar
              videoEnabled={videoEnabled}
              handleToggleVideo={handleToggleVideo}
              handleEndSimulation={handleEndSimulation}
              openHelpModal={() => {}}
              panelValue={panelValue}
              setPanelValue={setPanelValue}
              showSidePanel={showSidePanel}
              openSidePanel={() => setShowSidePanel(true)}
              scenarioData={scenarioData || {}}
              isExternalMode={false}
              isMobile={isMobile}
              isSpeaking={avatarSpeaking}
              onHelpTooltipClose={() => {}}
              subTitlesEnabled={subTitlesEnabled}
              handleToggleSubtitles={() => setSubTitlesEnabled((p) => !p)}
              isAudioOnly={audioOnlyActive}
              onTryVideo={() => {
                setAudioOnlyActive(false);
                setActiveBanner(null);
              }}
            />

            {showSidePanel && (
              <CallScreenSidePanel
                scenarioData={scenarioData || {}}
                isExternalMode={false}
                tab={panelValue}
                showSidePanel={showSidePanel}
                onClose={() => setShowSidePanel(false)}
                transcript={transcript}
                isMobile={isMobile}
              />
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
}
